import React, { useEffect, useState } from 'react';
import { Offcanvas, Table, Badge, Spinner, Button } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList } from 'recharts';
import { Phone, Eye } from 'lucide-react';
import moment from 'moment-timezone';
import { customerAnalyticsAPI } from '../../services';

const formatCurrency = (n) =>
  new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(n || 0);

const ProductLoyalCustomersPanel = ({
  show,
  onHide,
  product,
  queryParams,
  onShowCustomerHistory,
}) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !product) return;

    let cancelled = false;
    setLoading(true);
    customerAnalyticsAPI
      .getProductCustomers(product.menu_item_id, { ...queryParams, limit: 100 })
      .then((res) => {
        if (cancelled) return;
        setCustomers(res?.customers || []);
      })
      .catch((err) => {
        console.error('[ProductLoyalCustomersPanel]', err);
        if (!cancelled) setCustomers([]);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [show, product, queryParams]);

  if (!product) return null;

  const distribution = [
    { label: '1 lần', count: product.buyers_1x },
    { label: '2 lần', count: product.buyers_2x },
    { label: '3 lần', count: product.buyers_3x },
    { label: '4 lần', count: product.buyers_4x },
    { label: '≥5 lần', count: product.buyers_5x_plus },
  ];

  return (
    <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: 560 }}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <div className="fw-bold">{product.menu_item_name}</div>
          {product.category_name && <div className="small text-muted fw-normal">{product.category_name}</div>}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <div className="mb-4">
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted small">Phân phối số lần mua</span>
            <Badge bg="success">
              {((product.repurchase_rate || 0) * 100).toFixed(1)}% quay lại
            </Badge>
          </div>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distribution} margin={{ top: 20, right: 10, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" fontSize={12} />
                <YAxis fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#0d6efd" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="count" position="top" fontSize={11} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <h6 className="mb-3">Top khách đã mua ({customers.length})</h6>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" /> <span className="ms-2">Đang tải…</span>
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-4 text-muted">Chưa có khách nào.</div>
        ) : (
          <div className="table-responsive">
            <Table size="sm" hover className="align-middle">
              <thead className="table-light">
                <tr>
                  <th>Khách</th>
                  <th className="text-center">Số lần</th>
                  <th className="text-end">Chi tiêu</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.customer_phone}>
                    <td>
                      <div className="d-flex align-items-center">
                        <Phone size={12} className="me-1 text-muted" />
                        <div>
                          <div className="fw-semibold small">{c.customer_phone}</div>
                          {c.customer_name && <div className="text-muted" style={{ fontSize: 11 }}>{c.customer_name}</div>}
                          {c.last_purchase_at && (
                            <div className="text-muted" style={{ fontSize: 11 }}>
                              lần cuối: {moment(c.last_purchase_at).format('DD/MM/YYYY')}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="text-center">
                      <Badge bg={c.purchase_count >= 5 ? 'success' : c.purchase_count >= 3 ? 'primary' : 'secondary'}>
                        {c.purchase_count}
                      </Badge>
                    </td>
                    <td className="text-end small">{formatCurrency(c.total_spent)} ₫</td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0"
                        onClick={() => onShowCustomerHistory(c.customer_phone)}
                      >
                        <Eye size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default ProductLoyalCustomersPanel;
